import { useMutation, useQueryClient } from '@tanstack/react-query';

import { type AirActionTrackingLocation, useTrackRegeneratedShareLink } from '@air/analytics';
import { ShortUrl } from '@air/api';
import { type Asset, type Board, type ShortUrlResponse } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { getAssetShareLinkObjectId } from '~/components/SharePrivateClipModal/buildAssetShareLinkSlots';
import { patchAssetShareLinksListQueryData } from '~/swr-hooks/shortUrl/updateAssetShareLinkInListCache';
import { type AssetScopedShareLinkListItem } from '~/swr-hooks/shortUrl/useListAssetShareLinks';

export type AssetScopedShareLinkShortIdInfo = Pick<ShortUrlResponse, 'id' | 'permissions'> &
  Pick<AssetScopedShareLinkListItem, 'objectType' | 'objectId'>;

type RegenerateAssetScopedShortIdTarget = {
  objectId: string;
  objectType: AssetScopedShareLinkListItem['objectType'];
};

const getRegenerateAssetScopedShortIdTarget = ({
  assetId,
  shortIdInfo,
}: {
  assetId: Asset['id'];
  shortIdInfo: AssetScopedShareLinkShortIdInfo;
}): RegenerateAssetScopedShortIdTarget => ({
  objectType: shortIdInfo.objectType,
  objectId: getAssetShareLinkObjectId(shortIdInfo, assetId),
});

export type RegenerateAssetScopedShortIdVariables = {
  workspaceId: string;
  assetId: Asset['id'];
  shortIdInfo: AssetScopedShareLinkShortIdInfo;
  boardContext?: Board['id'];
  trackLocation: AirActionTrackingLocation;
};

export const useRegenerateAssetScopedShortId = () => {
  const queryClient = useQueryClient();
  const { trackRegeneratedShareLink } = useTrackRegeneratedShareLink();

  const regenerateAssetScopedShortId = useMutation({
    mutationFn: async ({
      assetId,
      shortIdInfo,
      trackLocation: _trackLocation,
      workspaceId,
      boardContext,
    }: RegenerateAssetScopedShortIdVariables) => {
      const { objectType } = getRegenerateAssetScopedShortIdTarget({ assetId, shortIdInfo });

      if (objectType === 'asset') {
        return ShortUrl.regenerateShortIdForAsset({
          workspaceId,
          shortId: shortIdInfo.id,
          permissions: shortIdInfo.permissions,
        });
      }

      return ShortUrl.regenerateShortIdForClip({
        workspaceId,
        shortId: shortIdInfo.id,
        permissions: shortIdInfo.permissions,
        boardContext,
      });
    },
    onSuccess: (regeneratedShortIdInfo, { assetId, shortIdInfo, trackLocation, workspaceId }) => {
      patchAssetShareLinksListQueryData(queryClient, { workspaceId, assetId }, shortIdInfo.id, regeneratedShortIdInfo, {
        newId: regeneratedShortIdInfo.id,
      });

      trackRegeneratedShareLink({ shortId: regeneratedShortIdInfo.id, location: trackLocation });
    },
    onError: (error, params) => {
      throw reportErrorToBugsnag({
        error,
        context: `Failed to regenerate short id for asset-scoped ${params.shortIdInfo.objectType} share link`,
        metadata: { data: params },
      });
    },
  });

  return {
    regenerateAssetScopedShortId,
  };
};
